import { AnyAction } from 'redux';
import {
  LOAD_USER_SUCCESS,
  LOGIN_FAILED,
  LOGIN_SUCCEEDED,
  LOGOUT,
  REQUEST_OAUTH_TOKENS,
  SET_LOGIN_ERROR_MESSAGE,
  UPDATE_USER_REFRESH_TOKEN,
  UPDATE_USER_TOKEN,
  IMPERSONATION_SUCCESS,
  IMPERSONATION_STOP,
  SET_TABLE_VISIBLE_COLUMNS,
  TOGGLE_PUSH_NOTIFICATION_TOAST,
} from '../actions';

export enum UserRole {
  Admin = 'ADMIN',
  BackofficeAgent = 'BACKOFFICE_AGENT',
  SuperAdmin = 'SUPERADMIN',
  AuditAgent = 'AUDIT_AGENT',
  Trader = 'TRADER',
  Affiliate = 'AFFILIATE',
  Leader = 'LEADER',
  Sales = 'SALES',
  GroupAdmin = 'GROUP_ADMIN',
}

export type VisibleTableKeys = 'CUSTOMERS_TABLE';

export enum Permission {
  CREATE_TRANSACTION = 'CREATE_TRANSACTION',
  VIEW_DASHBOARD = 'VIEW_DASHBOARD',
  VIEW_CUSTOMERS = 'VIEW_CUSTOMERS',
  VIEW_TRANSACTIONS = 'VIEW_TRANSACTIONS',
  VIEW_POSITIONS = 'VIEW_POSITIONS',
  VIEW_SINGLE_POSITION = 'VIEW_SINGLE_POSITION',
  VIEW_RISK = 'VIEW_RISK',
  VIEW_REPORTS = 'VIEW_REPORTS',
  VIEW_GROUPS = 'VIEW_GROUPS',
  MANAGE_GROUPS = 'MANAGE_GROUPS',
  VIEW_INSTRUMENTS = 'VIEW_INSTRUMENTS',
  VIEW_EMAILS = 'VIEW_EMAILS',
  MANAGE_EMAILS = 'MANAGE_EMAILS',
  VIEW_BOOKMARKS = 'VIEW_BOOKMARKS',
  VIEW_CUSTOMER_FIELD_CONFIGURATIONS = 'VIEW_CUSTOMER_FIELD_CONFIGURATIONS',
  VIEW_BONUSES = 'VIEW_BONUSES',
  VIEW_BANNERS = 'VIEW_BANNERS',
  VIEW_ORDERS = 'VIEW_ORDERS',
  VIEW_DOWNTIMES = 'VIEW_DOWNTIMES',
  VIEW_PAYMENT_PROVIDERS = 'VIEW_PAYMENT_PROVIDERS',
  VIEW_WALLETS = 'VIEW_WALLETS',
  VIEW_MASS_BONUSES = 'VIEW_MASS_BONUSES',
  MANAGE_MESSAGES = 'MANAGE_MESSAGES',
  VIEW_AFFILIATES = 'VIEW_AFFILIATES',
  VIEW_COINS = 'VIEW_COINS',
  MANAGE_COINS = 'MANAGE_COINS',
  VIEW_COUNTRIES = 'VIEW_COUNTRIES',
  VIEW_COMPETITIONS = 'VIEW_COMPETITIONS',
  MANAGE_COMPETITIONS = 'MANAGE_COMPETITIONS',
  MANAGE_TRANSACTIONS = 'MANAGE_TRANSACTIONS',
  MANAGE_CUSTOMER_KYC_DOCUMENTS = 'MANAGE_CUSTOMER_KYC_DOCUMENTS',
  MANAGE_WALLETS = 'MANAGE_WALLETS',
  REVEAL_MT5_PASSWORD = 'REVEAL_MT5_PASSWORD',
  MANAGE_TEST_CUSTOMERS = 'MANAGE_TEST_CUSTOMERS',
  MANAGE_CUSTOMERS = 'MANAGE_CUSTOMERS',
  MANAGE_CUSTOMER_PASSWORDS = 'MANAGE_CUSTOMER_PASSWORDS',
  MANAGE_POSITIONS = 'MANAGE_POSITIONS',
  CLOSE_POSITIONS = 'CLOSE_POSITIONS',
  VIEW_SALES_PEOPLE = 'VIEW_SALES_PEOPLE',
  VIEW_OWN_SALES_PERFORMANCE = 'VIEW_OWN_SALES_PERFORMANCE',
  VIEW_REFERRER_FILTER = 'VIEW_REFERRER_FILTER',
  MANAGE_CALL_PROVIDERS = 'MANAGE_CALL_PROVIDERS',
  VIEW_SALES_DESK = 'VIEW_SALES_DESK',
  MANAGE_SALES_DESK = 'MANAGE_SALES_DESK',
  SEND_MESSAGES = 'SEND_MESSAGES',
  VIEW_LEADS = 'VIEW_LEADS',
  MANAGE_LEADS = 'MANAGE_LEADS',
  MANAGE_LEADS_SALES_PERSON = 'MANAGE_LEADS_SALES_PERSON',
  UPLOAD_LEADS = 'UPLOAD_LEADS',
  CLAIM_LEADS = 'CLAIM_LEADS',
  IMPERSONATE = 'IMPERSONATE',
  MANAGE_MAX_RISK = 'MANAGE_MAX_RISK',
  VIEW_EXCHANGE_RATE = 'VIEW_EXCHANGE_RATE',
  MANAGE_SALES_STATUS = 'MANAGE_SALES_STATUS',
  MANAGE_SALES_STATUS_CUSTOMER = 'MANAGE_SALES_STATUS_CUSTOMER',
  VIEW_SENSITIVE_CUSTOMER_DATA = 'VIEW_SENSITIVE_CUSTOMER_DATA',
  MANAGE_CUSTOMER_SECURITY_LEVEL = 'MANAGE_CUSTOMER_SECURITY_LEVEL',
  MANAGE_CUSTOMER_ONBOARDING_STEPS = 'MANAGE_CUSTOMER_ONBOARDING_STEPS',
  MANAGE_CURRENCY_CONVERSIONS = 'MANAGE_CURRENCY_CONVERSIONS',
  MANAGE_ORDERS = 'MANAGE_ORDERS',
  BULK_EDIT_LEADS = 'BULK_EDIT_LEADS',
  VIEW_AUDIT_LOGS = 'VIEW_AUDIT_LOGS',
  VIEW_ADAPTERS = 'VIEW_ADAPTERS',
  MANAGE_ADAPTERS = 'MANAGE_ADAPTERS',
  MANAGE_GENERAL_SETTINGS = 'MANAGE_GENERAL_SETTINGS',
  MANAGE_COMMISSION_MODELS = 'MANAGE_COMMISSION_MODELS',
  VIEW_COMMISSION_MODELS = 'VIEW_COMMISSION_MODELS',
  VIEW_REFERRAL_TREE = 'VIEW_REFERRAL_TREE',
  VIEW_SUM_SUB_ENABLED = 'VIEW_SUM_SUB_ENABLED',
  VIEW_SYMBOLS = 'VIEW_SYMBOLS',
  MANAGE_CUSTOMER_SALES_DESK = 'MANAGE_CUSTOMER_SALES_DESK',
  VIEW_GENERAL_SETTINGS = 'VIEW_GENERAL_SETTINGS',
  VIEW_TRADING_OPTIONS = 'VIEW_TRADING_OPTIONS',
  MANAGE_TRADING_OPTIONS = 'MANAGE_TRADING_OPTIONS',
  VIEW_PROP_CHALLENGES = 'VIEW_PROP_CHALLENGES',
  MANAGE_PROP_CHALLENGES = 'MANAGE_PROP_CHALLENGES',
  MANAGE_FUNDED_ACCOUNT_TYPES = 'MANAGE_FUNDED_ACCOUNT_TYPES',
  VIEW_FUNDED_ACCOUNT_TYPES = 'VIEW_FUNDED_ACCOUNT_TYPES',
  MANAGE_PROP_FUNNELS = 'MANAGE_PROP_FUNNELS',
  VIEW_PROP_FUNNELS = 'VIEW_PROP_FUNNELS',
  VIEW_OWN_CUSTOMER_TRANSACTIONS = 'VIEW_OWN_CUSTOMER_TRANSACTIONS',
  VIEW_ADMIN_NOTIFICATIONS = 'VIEW_ADMIN_NOTIFICATIONS',
  VIEW_OWN_NOTIFICATIONS = 'VIEW_OWN_NOTIFICATIONS',
  VIEW_COMMUNICATIONS = 'VIEW_COMMUNICATIONS',
  VIEW_CUSTOMER_PERSONAL_INFORMATION = 'VIEW_CUSTOMER_PERSONAL_INFORMATION',
  QUALIFY_AFFILIATE_TRANSACTIONS = 'QUALIFY_AFFILIATE_TRANSACTIONS',
  VIEW_DEPOSIT_LINK = 'VIEW_DEPOSIT_LINK',
  CAN_GENERATE_AUTOLOGIN_LINK = 'CAN_GENERATE_AUTOLOGIN_LINK',
  MANAGE_ROLE_PERMISSIONS = 'MANAGE_ROLE_PERMISSIONS',
  MANAGE_CUSTOMER_COMMENTS = 'MANAGE_CUSTOMER_COMMENTS',
  DELETE_CUSTOMER_COMMENTS = 'DELETE_CUSTOMER_COMMENTS',
  ACCESS_CALL_PROVIDERS = 'ACCESS_CALL_PROVIDERS',
}

export interface IUserState {
  loggingIn: boolean;
  accountId: string | null;
  token: string | null;
  refreshToken: string | null;
  id: string | null;
  name: string | null;
  email: string | null;
  mobilenumber: string | null;
  refKey: string | null;
  avatarUrl: string | null;
  loginError: string | null;
  roles: Array<UserRole>;
  dismissPushNotificationToast: boolean;
  communicationLanguage: string;
  impersonation?: {
    adminToken: string;
  };
  permissions: Array<Permission>;
  visibleTableColumns: {
    [tableKey in VisibleTableKeys]: Array<string>;
  };
  groups: Array<number>;
}

const initialCustomerTableVisibleColumns: Array<string> = [
  'name',
  'activity',
  'country',
  'totalCoinsEarned',
  'createdAt',
  'source',
  'depositTotal',
  'withdrawalTotal',
  'netDeposit',
  'totalTradingVolume',
  'positionsCount',
  'totalReferrals',
  'status',
  'lastTradingActivityDate',
];

const initialState: IUserState = {
  loggingIn: false,
  accountId: null,
  token: null,
  refreshToken: null,
  id: null,
  name: null,
  email: null,
  mobilenumber: null,
  refKey: null,
  avatarUrl: null,
  loginError: null,
  dismissPushNotificationToast: false,
  roles: [],
  communicationLanguage: 'en',
  permissions: [],
  visibleTableColumns: {
    CUSTOMERS_TABLE: initialCustomerTableVisibleColumns,
  },
  groups: [],
};

function userReducer(state: IUserState | undefined, action: AnyAction): IUserState {
  if (typeof state === 'undefined') {
    return initialState;
  }

  switch (action.type) {
    case LOGOUT:
      return {
        ...state,
        token: null,
        refreshToken: null,
        name: null,
        avatarUrl: null,
        loginError: null,
        impersonation: undefined,
      };

    case LOGIN_SUCCEEDED:
      return {
        ...state,
        loggingIn: false,
        id: action.user.id,
        accountId: action.user.accountId,
        token: action.token,
        refreshToken: action.refreshToken,
        name: action.user.name,
        email: action.user.email,
        mobilenumber: action.user.mobilenumber,
        refKey: action.user.refKey,
        avatarUrl: action.user.avatarUrl,
        roles: action.user.roles,
        loginError: null,
        impersonation: undefined,
        communicationLanguage: action.user.communicationLanguage,
        permissions: action.user.permissions,
        groups: action.groups,
      };

    case LOGIN_FAILED:
      return {
        ...state,
        loggingIn: false,
        token: null,
        name: null,
        email: null,
        mobilenumber: null,
        refKey: null,
        avatarUrl: null,
        roles: [],
        loginError: action.message,
        impersonation: undefined,
      };

    case SET_LOGIN_ERROR_MESSAGE:
      return {
        ...state,
        loginError: action.message,
      };

    case IMPERSONATION_SUCCESS:
      return {
        ...state,
        impersonation: {
          adminToken: state.token!,
        },
        accountId: action.accountId,
        token: action.token,
        name: action.user.name,
        email: action.user.email,
        mobilenumber: action.user.mobilenumber,
        refKey: action.user.refKey,
        avatarUrl: action.user.avatarUrl,
        roles: action.user.roles,
        permissions: action.user.permissions,
      };

    case IMPERSONATION_STOP:
      return {
        ...state,
        impersonation: undefined,
        token: state.impersonation!.adminToken,
      };

    case UPDATE_USER_TOKEN:
      return {
        ...state,
        token: action.token,
      };

    case UPDATE_USER_REFRESH_TOKEN:
      return {
        ...state,
        refreshToken: action.refreshToken,
      };

    case LOAD_USER_SUCCESS:
      return {
        ...state,
        accountId: action.user.accountId,
        name: action.user.name,
        email: action.user.email,
        mobilenumber: action.user.mobilenumber,
        refKey: action.user.refKey,
        avatarUrl: action.user.avatarUrl,
        roles: action.user.roles,
        communicationLanguage: action.user.communicationLanguage,
        permissions: action.user.permissions,
        groups: action.groups,
      };

    case REQUEST_OAUTH_TOKENS:
      return {
        ...state,
        loggingIn: true,
      };
    case SET_TABLE_VISIBLE_COLUMNS: {
      return {
        ...state,
        visibleTableColumns: {
          ...initialState.visibleTableColumns,
          [action.payload.tableKey]: action.payload.cols,
        },
      };
    }

    case TOGGLE_PUSH_NOTIFICATION_TOAST: {
      return {
        ...state,
        dismissPushNotificationToast: action.payload,
      };
    }

    default:
      return state;
  }
}

export default userReducer;
